/**
 * Canonical safe-default shop hours (Batch 8B).
 *
 * Used when:
 *   - the operator submits onboarding without typing hours (Batch 8B
 *     removed the "at least one day" requirement);
 *   - the public-listing lookup found no match and no hours provider
 *     was configured (`accept-team-invitation`, etc., already
 *     duplicate this constant inline — those copies stay for now to
 *     avoid touching invitation behavior in this batch).
 *
 * Format matches `shop_profile.hours` jsonb shape:
 *   { monday: { open: 'HH:MM', close: 'HH:MM' }, ... }
 *
 * Closed days are intentionally omitted (`undefined`) rather than
 * stored as `null` — the existing schema (`weeklyHoursSchema`) marks
 * each day entry optional, so omission is the canonical "closed"
 * representation for the wizard. Downstream consumers
 * (`afterHoursConfirmationEscalation`, `JobTimerControls`) treat
 * missing days as closed.
 */

export type DayHours = { open: string; close: string };
export type ShopHoursMap = Record<string, DayHours | undefined>;

/** Monday–Friday, 08:00–17:00; weekends closed. */
export const DEFAULT_SHOP_HOURS_MON_FRI_8_5: ShopHoursMap = {
  monday: { open: '08:00', close: '17:00' },
  tuesday: { open: '08:00', close: '17:00' },
  wednesday: { open: '08:00', close: '17:00' },
  thursday: { open: '08:00', close: '17:00' },
  friday: { open: '08:00', close: '17:00' },
};

/**
 * True when no day in the map carries a usable open+close pair.
 * Treats `{}`, `{ monday: undefined }`, and `{ monday: { open: '', close: '' } }`
 * all as empty. Used by `commitWizard` to decide whether to apply
 * defaults, and by `lookup-shop-public-info` to decide whether it's
 * safe to fill from a public listing.
 */
export function isHoursMapEmpty(hours: ShopHoursMap | null | undefined): boolean {
  if (!hours || typeof hours !== 'object') return true;
  for (const day of Object.values(hours)) {
    if (day && typeof day.open === 'string' && typeof day.close === 'string'
        && day.open.length > 0 && day.close.length > 0) {
      return false;
    }
  }
  return true;
}

/**
 * Returns the input hours when non-empty, otherwise a fresh clone of
 * `DEFAULT_SHOP_HOURS_MON_FRI_8_5`. Caller MUST also record the source
 * (`'manual'` vs `'default'`) so the inference card / Team Management
 * can show provenance.
 */
export function withDefaultHoursIfEmpty(hours: ShopHoursMap | null | undefined): ShopHoursMap {
  if (!isHoursMapEmpty(hours)) return hours as ShopHoursMap;
  return { ...DEFAULT_SHOP_HOURS_MON_FRI_8_5 };
}
